/**
 * Single source of truth for primary + secondary nav items.
 *
 * Consumed by:
 *  - AppLayout desktop sidebar (`NAV_ALL`)
 *  - MobileBottomNav bottom-bar primary tabs (`NAV_PRIMARY_MOBILE`)
 *  - MobileBottomNav "More" sheet secondary grid (`NAV_SECONDARY_MOBILE`)
 *  - Admin quicknav (`ADMIN_ITEMS`)
 *
 * Adding a route here automatically wires it into every nav surface.
 * Icons are imported from lucide-react and referenced by name (not
 * component instance) so this file has no React dependency — safe to
 * import from anywhere.
 */

import {
  LayoutDashboard, Wallet, Receipt, Landmark, CreditCard, CalendarDays,
  Target, TrendingUp, PieChart, BarChart3, Sparkles, Bot, Sparkle,
  Flame, Users, Share2,
} from "lucide-react";

// Full sidebar nav (desktop). Order matters — this drives sidebar order.
export const NAV_ALL = [
  { to: "/dashboard",     i18nKey: "dashboard",     icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/income",        i18nKey: "income",        icon: Wallet,          testId: "nav-income" },
  { to: "/expenses",      i18nKey: "expenses",      icon: Receipt,         testId: "nav-expenses" },
  { to: "/loans",         i18nKey: "loans",         icon: Landmark,        testId: "nav-loans" },
  { to: "/subscriptions", i18nKey: "subscriptions", icon: CreditCard,      testId: "nav-subscriptions" },
  { to: "/bills",         i18nKey: "bills",         icon: CalendarDays,    testId: "nav-bills" },
  { to: "/goals",         i18nKey: "goals",         icon: Target,          testId: "nav-goals" },
  { to: "/investments",   i18nKey: "investments",   icon: TrendingUp,      testId: "nav-investments" },
  { to: "/budget",        i18nKey: "budget",        icon: PieChart,        testId: "nav-budget" },
  // Pro-only from here down
  { to: "/reports",       i18nKey: "reports",       icon: BarChart3,       testId: "nav-reports",    proOnly: true },
  { to: "/optimizer",     i18nKey: "optimizer",     icon: Sparkles,        testId: "nav-optimizer",  proOnly: true },
  { to: "/advisor",       i18nKey: "advisor",       icon: Bot,             testId: "nav-advisor",    proOnly: true },
  { to: "/streak",        i18nKey: "streak",        icon: Flame,           testId: "nav-streak",     proOnly: true },
  { to: "/circles",       i18nKey: "circles",       icon: Users,           testId: "nav-circles",    proOnly: true },
  { to: "/wrapup",        i18nKey: "wrapup",        icon: Sparkle,         testId: "nav-wrapup",     proOnly: true },
  { to: "/share",         i18nKey: "sharehub",      icon: Share2,          testId: "nav-share",      proOnly: true },
];

// Primary mobile bottom-bar tabs (3 items — FAB sits between #2 and #3).
export const NAV_PRIMARY_MOBILE = [
  { to: "/dashboard", i18nKey: "dashboard", icon: LayoutDashboard, testId: "bottomnav-dashboard" },
  { to: "/expenses",  i18nKey: "expenses",  icon: Receipt,         testId: "bottomnav-expenses" },
  { to: "/reports",   i18nKey: "reports",   icon: BarChart3,       testId: "bottomnav-reports", proOnly: true },
];

// Everything not in NAV_PRIMARY_MOBILE lands in the "More" sheet.
const _PRIMARY_ROUTES = new Set(NAV_PRIMARY_MOBILE.map((n) => n.to));
export const NAV_SECONDARY_MOBILE = NAV_ALL
  .filter((n) => !_PRIMARY_ROUTES.has(n.to))
  .map((n) => ({ to: n.to, icon: n.icon, i18nKey: n.i18nKey, proOnly: !!n.proOnly }));

export const ADMIN_ITEMS = [
  { to: "/admin/metrics",  label: "Metrics",  testId: "admin-nav-metrics" },
  { to: "/admin/users",    label: "Users",    testId: "admin-nav-users" },
  { to: "/admin/waitlist", label: "Waitlist", testId: "admin-nav-waitlist" },
  { to: "/admin/audit",    label: "Audit",    testId: "admin-nav-audit" },
  { to: "/admin/trials",   label: "Trials",   testId: "admin-nav-trials" },
];
